import { answerStateMap, smallQuestionTypeEnum } from '@/views/PopQuiz/enum'
import { getUserListByIds } from '@/api/common'
import { setAnswerForSmallList } from '@/views/PaperCollect/utils'
import { chooseSmallQuestionTypeList, readSmallQuestionTypeList } from '@/views/PopQuiz/enum'

const state = () => ({
  currentClazzId: '',
  // 作答状态：1初始化，2作答中，3作答结束
  answerState: answerStateMap.init,
  bigQuestionList: [],
  studentList: [],
  // 学生姓名映射
  studentNameMap: {},
  currentStudentId: '', //当前正在查看的学生id
  singleModeBigIndex: 0, // 单题模式下当前大题的索引，用于听力
  singleModeSmallIndex: 0, // 单题模式下当前小题的索引，用于朗读、笔试
  // 是否显示释义
  showTranslate: false,
  // 放映模式
  projectionMode: false,
  // 当前激活的 tab 索引（0: 检测试题, 1: 检测报告）
  activeTab: 0,
  // 检测报告 tab 中当前选中的班级ID
  reportTabCurrentClassId: '',
  curMedia: {
    url: '' // 当前播放的媒体资源
  },
  // 当前激活的 tab 索引（0: 我的资源, 1: 本校资源）
  activeResourceTab: 0,
  // ── V2 检测页专用状态 ──
  // 画布数据列表（V2 特有，canvasJson 解析后的数组）
  canvasList: [],
  // 当前画布索引
  activeCanvasIndex: 0,
  // 下发模式：null 未选择 / 'all' 下发全部 / 'single' 逐题下发
  answerMode: null,
  // 逐题模式当前题全局索引（从 1 开始，与题序一致）
  currentQuestionIndex: 0,
  // 快捷下发开关
  quickDispatchEnabled: false,
  // 两道题之间过渡状态（逐题模式结束当前题后、下发下一题前）
  isBetweenQuestions: false,
  // 画布标注操作历史
  annotationHistory: []
})

const getters = {
  currentClazzId: state => state.currentClazzId,
  isAnswering: state => state.answerState === answerStateMap.answering,
  isAnswerEnd: state => state.answerState === answerStateMap.answerEnd,
  bigQuestionList: state => state.bigQuestionList,
  // 单题模式下当前大题的索引，用于听力
  singleModeBigIndex: state => state.singleModeBigIndex,
  // 当前大题，用于听力题单题模式
  currentBig: state => state.bigQuestionList[state.singleModeBigIndex],
  // 扁平化的小题列表
  smallQuestionList: (state, getters) => getters.bigQuestionList.map(big => big.smallList).flat(),
  // 单题模式下当前小题的索引，用于朗读、笔试
  singleModeSmallIndex: state => state.singleModeSmallIndex,
  // 当前小题，用于朗读题，笔试题单题模式
  currentSmall: (state, getters) => getters.smallQuestionList[state.singleModeSmallIndex],
  studentList: state => state.studentList,
  // 绑定了答题器的学生列表
  bindDTQStudentList: state => state.studentList.filter(student => student.isBindAnswerDevice),
  studentNameMap: state => state.studentNameMap,
  currentStudentId: state => state.currentStudentId,
  showTranslate: state => state.showTranslate,
  // 放映模式
  projectionMode: state => state.projectionMode,
  // 当前激活的 tab 索引
  activeTab: state => state.activeTab,
  // 检测报告 tab 中当前选中的班级ID
  reportTabCurrentClassId: state => state.reportTabCurrentClassId,
  curMedia: state => state.curMedia,
  activeResourceTab: state => state.activeResourceTab,
  // ── V2 检测页专用 getters ──
  canvasList: state => state.canvasList,
  activeCanvasIndex: state => state.activeCanvasIndex,
  answerMode: state => state.answerMode,
  currentQuestionIndex: state => state.currentQuestionIndex,
  quickDispatchEnabled: state => state.quickDispatchEnabled,
  isBetweenQuestions: state => state.isBetweenQuestions,
  annotationHistory: state => state.annotationHistory,
  // V2: 试卷总小题数
  totalQuestionCount: (state, getters) =>
    getters.bigQuestionList.reduce((sum, big) => sum + (big.smallList ? big.smallList.length : 0), 0),
  // V2: 当前题已提交学生数
  answeredStudentCount: (state, getters) => {
    if (!getters.currentQuestionIndex || getters.currentQuestionIndex < 1) return 0
    const currentSmall = getters.smallQuestionList[getters.currentQuestionIndex - 1]
    return currentSmall ? (currentSmall.doneCount || 0) : 0
  },
  // V2: 当前画布数据
  currentCanvas: state => state.canvasList[state.activeCanvasIndex] || null,
  // V2: 是否为最后一题
  isLastQuestion: (state, getters) =>
    getters.currentQuestionIndex >= getters.totalQuestionCount
}

const mutations = {
  SET_CURRENT_CLAZZ_ID(state, payload) {
    state.currentClazzId = payload
  },
  SET_ANSWER_STATE(state, payload) {
    state.answerState = payload
  },
  SET_BIG_QUESTION_LIST(state, payload) {
    state.bigQuestionList = payload
  },
  SET_STUDENT_LIST(state, payload) {
    state.studentList = payload
  },
  SET_STUDENT_NAME_MAP(state, payload) {
    state.studentNameMap = payload
  },
  SET_CURRENT_STUDENT_ID(state, payload) {
    state.currentStudentId = payload
  },
  SET_SINGLE_MODE_BIG_INDEX(state, payload) {
    state.singleModeBigIndex = payload
  },
  SET_SINGLE_MODE_SMALL_INDEX(state, payload) {
    state.singleModeSmallIndex = payload
  },
  SET_SHOW_TRANSLATE(state, payload) {
    state.showTranslate = payload
  },
  SET_PROJECTION_MODE(state, payload) {
    state.projectionMode = payload
  },
  SET_ACTIVE_TAB(state, payload) {
    state.activeTab = payload
  },
  SET_REPORT_TAB_CURRENT_CLASS_ID(state, payload) {
    state.reportTabCurrentClassId = payload
  },
  SET_CUR_MEDIA(state, payload) {
    state.curMedia = payload
  },
  SET_ACTIVE_RESOURCE_TAB(state, payload) {
    state.activeResourceTab = payload
  },
  // ── V2 检测页专用 mutations ──
  SET_CANVAS_LIST(state, payload) {
    state.canvasList = payload
  },
  SET_ACTIVE_CANVAS_INDEX(state, payload) {
    state.activeCanvasIndex = payload
  },
  SET_ANSWER_MODE(state, payload) {
    state.answerMode = payload
  },
  SET_CURRENT_QUESTION_INDEX(state, payload) {
    state.currentQuestionIndex = payload
  },
  SET_QUICK_DISPATCH_ENABLED(state, payload) {
    state.quickDispatchEnabled = payload
  },
  SET_IS_BETWEEN_QUESTIONS(state, payload) {
    state.isBetweenQuestions = payload
  },
  SET_ANNOTATION_HISTORY(state, payload) {
    state.annotationHistory = payload
  }
}

const actions = {
  setCurrentClazzId({ commit }, payload) {
    commit('SET_CURRENT_CLAZZ_ID', payload)
  },
  setAnswerState({ commit }, payload) {
    commit('SET_ANSWER_STATE', payload)
  },
  setBigQuestionList({ commit }, payload) {
    commit('SET_BIG_QUESTION_LIST', payload)
  },
  setStudentList({ commit }, payload) {
    commit('SET_STUDENT_LIST', payload)
  },
  setStudentNameMap({ commit }, payload) {
    commit('SET_STUDENT_NAME_MAP', payload)
  },
  setCurrentStudentId({ commit }, payload) {
    commit('SET_CURRENT_STUDENT_ID', payload)
  },
  setSingleModeBigIndex({ commit }, payload) {
    commit('SET_SINGLE_MODE_BIG_INDEX', payload)
  },
  setSingleModeSmallIndex({ commit }, payload) {
    commit('SET_SINGLE_MODE_SMALL_INDEX', payload)
  },
  setShowTranslate({ commit }, payload) {
    commit('SET_SHOW_TRANSLATE', payload)
  },
  setProjectionMode({ commit }, payload) {
    commit('SET_PROJECTION_MODE', payload)
  },
  setActiveTab({ commit }, payload) {
    commit('SET_ACTIVE_TAB', payload)
  },
  setReportTabCurrentClassId({ commit }, payload) {
    commit('SET_REPORT_TAB_CURRENT_CLASS_ID', payload)
  },
  setCurMedia({ commit }, payload) {
    commit('SET_CUR_MEDIA', payload)
  },
  setActiveResourceTab({ commit }, payload) {
    commit('SET_ACTIVE_RESOURCE_TAB', payload)
  },
  // ── V2 检测页专用 actions ──
  setCanvasList({ commit }, payload) {
    commit('SET_CANVAS_LIST', payload)
  },
  setActiveCanvasIndex({ commit }, payload) {
    commit('SET_ACTIVE_CANVAS_INDEX', payload)
  },
  setAnswerMode({ commit }, payload) {
    commit('SET_ANSWER_MODE', payload)
  },
  setCurrentQuestionIndex({ commit }, payload) {
    commit('SET_CURRENT_QUESTION_INDEX', payload)
  },
  setQuickDispatchEnabled({ commit }, payload) {
    commit('SET_QUICK_DISPATCH_ENABLED', payload)
  },
  setIsBetweenQuestions({ commit }, payload) {
    commit('SET_IS_BETWEEN_QUESTIONS', payload)
  },
  setAnnotationHistory({ commit }, payload) {
    commit('SET_ANNOTATION_HISTORY', payload)
  },
  resetData({ dispatch }) {
    dispatch('setAnswerState', answerStateMap.init)
    dispatch('setBigQuestionList', [])
    dispatch('setStudentList', [])
    dispatch('setStudentNameMap', {})
    dispatch('setSingleModeBigIndex', 0)
    dispatch('setSingleModeSmallIndex', 0)
    // ── V2 检测页状态复位 ──
    dispatch('setCanvasList', [])
    dispatch('setActiveCanvasIndex', 0)
    dispatch('setAnswerMode', null)
    dispatch('setCurrentQuestionIndex', 0)
    dispatch('setQuickDispatchEnabled', false)
    dispatch('setIsBetweenQuestions', false)
    dispatch('setAnnotationHistory', [])
  },
  // 设置学生姓名映射表
  async setAnswerStudentNameMap({ getters, dispatch }) {
    const userIds = getters.studentList.map(item => item.id)
    const { data = [] } = await getUserListByIds({ userIds })
    const studentNameMap = {}
    data.forEach(item => {
      studentNameMap[item.id] = item.realName || item.name || '-'
    })
    dispatch('setStudentNameMap', studentNameMap)
  },
  // 设置作答和未作答学生的名字
  async setAnswerStudentName({ getters, dispatch }) {
    const bigQuestionList = getters.bigQuestionList
    for (let i = 0; i < bigQuestionList.length; i++) {
      const big = bigQuestionList[i]
      big.smallList = setAnswerForSmallList(big.smallList)
      big.smallList.forEach(small => {
        if (small.notDoneUserId?.length > 0) {
          small.notDoneUserName = small.notDoneUserId.map(id => getters.studentNameMap[id]).join('，')
        }
        // 如果是多选并且设置了答案，则要找出回答正确，回答不准确和回答错误的学生
        if (small.type === smallQuestionTypeEnum.multipleChoose && small.answer) {
          // 作答的学生
          const answerUserIds = Array.from(new Set(small.optionsTypeList.map(option => option.userId).flat()))
          // 正确选项
          const correctOptionsTypeList = small.optionsTypeList.filter(item => small.answer.includes(item.id))
          console.log(answerUserIds)
          const correctUserName = []
          const wrongUserName = []
          const inaccurateUserList = []
          answerUserIds.forEach(userId => {
            const userName = getters.studentNameMap[userId]
            const answerOptions = small.optionsTypeList.filter(item => item.userId?.includes(userId))
            const isCorrect = correctOptionsTypeList.every(item => item.userId?.includes(userId))
            // 回答正确：选择的答案必须包含所有正确答案，并且选择答案数量与正确答案数量一致
            if (isCorrect && answerOptions.length === correctOptionsTypeList.length) {
              correctUserName.push(userName)
              return
            }
            // 回答错误
            const isWrong = correctOptionsTypeList.every(item => !item.userId?.includes(userId))
            if (isWrong) {
              wrongUserName.push(userName)
              return
            }
            // 回答不准确
            inaccurateUserList.push({
              userName,
              answerOptions
            })
          })
          small.correctUserName = correctUserName
          small.wrongUserName = wrongUserName
          small.inaccurateUserList = inaccurateUserList
        }

        if (chooseSmallQuestionTypeList.includes(small.type)) {
          small.optionsTypeList.forEach(option => {
            if (option.userId?.length > 0) {
              option.userName = option.userId.map(id => getters.studentNameMap[id]).join('，')
            }
          })
        } else if (readSmallQuestionTypeList.includes(small.type) && small.answerList) {
          Object.entries(small.answerList).forEach(([userId, answerItem]) => {
            answerItem.userName = getters.studentNameMap[userId]
          })
        }
      })
      bigQuestionList[i] = big
    }
    dispatch('setBigQuestionList', bigQuestionList)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
